import { useAuth } from '@/contexts/AuthContext'
import { notificationService } from '@/services/notificationService'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { notificationKeys } from './query-keys'

/**
 * Company-Aware Notification Mutation Hooks
 * Following cursor rules patterns with optimistic updates and proper error handling
 * Clean Code: Descriptive names, consistent patterns, proper toast notifications
 * Company Context: Includes user's company context for proper cache management
 */

export const useMarkAsRead = (userId: string, platform: 'web' | 'mobile' = 'web') => {
  const { userData } = useAuth()
  const companyId = userData?.companyId
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) => {
      if (!notificationId || notificationId.trim() === '') {
        throw new Error('Invalid notification ID')
      }

      if (notificationId.startsWith('follow-request-')) {
        throw new Error('Cannot mark transformed request as read')
      }

      return notificationService.markAsRead(notificationId)
    },
    onMutate: async (notificationId: string) => {
      if (!notificationId || notificationId.trim() === '') {
        return { previousNotifications: null, previousCount: null, notificationId }
      }

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.list(userId, platform, companyId) })
      await queryClient.cancelQueries({ queryKey: notificationKeys.count(userId, platform, companyId) })

      // Snapshot previous values
      const previousNotifications = queryClient.getQueryData(notificationKeys.list(userId, platform, companyId))
      const previousCount = queryClient.getQueryData(notificationKeys.count(userId, platform, companyId))

      // Optimistically update notifications list
      queryClient.setQueryData(
        notificationKeys.list(userId, platform, companyId),
        (old: any[]) =>
          old
            ? old
                .map((notification) =>
                  notification?.id === notificationId ? { ...notification, read: true } : notification,
                )
                .filter(Boolean)
            : [], // Filter out any undefined/null notifications
      )

      // Optimistically update count (only if we found a notification to mark as read)
      const hadUnreadNotification =
        Array.isArray(previousNotifications) && previousNotifications.some((n) => n?.id === notificationId && !n?.read)

      if (hadUnreadNotification) {
        queryClient.setQueryData(notificationKeys.count(userId, platform, companyId), (old: number) =>
          old ? Math.max(0, old - 1) : 0,
        )
      }

      return { previousNotifications, previousCount, notificationId }
    },
    onError: (error, variables, context) => {
      // Revert optimistic updates
      if (context?.previousNotifications) {
        queryClient.setQueryData(notificationKeys.list(userId, platform, companyId), context.previousNotifications)
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(notificationKeys.count(userId, platform, companyId), context.previousCount)
      }

      toast.error('Failed to mark notification as read')
    },
    onSuccess: () => {
      // Invalidate related queries to ensure consistency after a brief delay
      // This allows Firestore to process the update and trigger real-time subscriptions
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: notificationKeys.list(userId, platform, companyId) })
        queryClient.invalidateQueries({ queryKey: notificationKeys.count(userId, platform, companyId) })
        queryClient.invalidateQueries({ queryKey: notificationKeys.countByType(userId, platform, companyId) })
        queryClient.invalidateQueries({ queryKey: notificationKeys.userNotifications(userId, platform, companyId) })
      }, 100)
    },
  })
}

export const useMarkAllAsRead = (userId: string, platform: 'web' | 'mobile' = 'web') => {
  const { userData } = useAuth()
  const companyId = userData?.companyId
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(userId, platform, companyId),
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: notificationKeys.list(userId, platform, companyId) })
      await queryClient.cancelQueries({ queryKey: notificationKeys.count(userId, platform, companyId) })

      // Snapshot previous values
      const previousNotifications = queryClient.getQueryData(notificationKeys.list(userId, platform, companyId))
      const previousCount = queryClient.getQueryData(notificationKeys.count(userId, platform, companyId))

      // Optimistically update all notifications to read
      queryClient.setQueryData(notificationKeys.list(userId, platform, companyId), (old: any[]) =>
        old ? old.map((notification) => ({ ...notification, read: true })) : [],
      )

      // Optimistically set count to 0
      queryClient.setQueryData(notificationKeys.count(userId, platform, companyId), 0)

      return { previousNotifications, previousCount }
    },
    onError: (error, variables, context) => {
      // Revert optimistic updates
      if (context?.previousNotifications) {
        queryClient.setQueryData(notificationKeys.list(userId, platform, companyId), context.previousNotifications)
      }
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(notificationKeys.count(userId, platform, companyId), context.previousCount)
      }

      toast.error('Failed to mark all notifications as read')
    },
    onSuccess: () => {
      // Invalidate all notification-related queries to ensure consistency
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: notificationKeys.list(userId, platform, companyId) })
        queryClient.invalidateQueries({ queryKey: notificationKeys.count(userId, platform, companyId) })
        queryClient.invalidateQueries({ queryKey: notificationKeys.countByType(userId, platform, companyId) })
        queryClient.invalidateQueries({ queryKey: notificationKeys.userNotifications(userId, platform, companyId) })
        // Also invalidate the broader notification keys to catch any cached data
        queryClient.invalidateQueries({ queryKey: notificationKeys.lists() })
        queryClient.invalidateQueries({ queryKey: notificationKeys.counts() })
      }, 100)

      toast.success('All notifications marked as read')
    },
  })
}

export const useCreateInteractionEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: notificationService.createInteractionEvent,
    onSuccess: () => {
      // Invalidate all notification queries to refresh data
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
    onError: (error) => {
      toast.error('Failed to create notification')
    },
  })
}

export const useCreateRequestEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: notificationService.createRequestEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
    onError: (error) => {
      toast.error('Failed to create notification')
    },
  })
}

export const useCreateSupportEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: notificationService.createSupportEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
    onError: (error) => {
      toast.error('Failed to create notification')
    },
  })
}

export const useCreateCompanyAnnouncementEvent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: notificationService.createCompanyAnnouncementEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
      toast.success('Company announcement sent')
    },
    onError: (error) => {
      toast.error('Failed to send company announcement')
    },
  })
}
